import React, { useEffect, useMemo, useRef, useState } from 'react';
import AppToast from './AppToast';
import { managementAPI } from '../services/api';
import { formatLocalDateYmd } from '../utils/localCalendarDate';

const OwnerEmployees = ({ selectedCompanyId }) => {
  const weekdayLabel = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteEmployeesBySite, setSiteEmployeesBySite] = useState({});
  const [shiftsBySite, setShiftsBySite] = useState({});
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [shiftAssignmentsByFactoryEmployee, setShiftAssignmentsByFactoryEmployee] = useState({});
  const [savingShiftByFactoryEmployee, setSavingShiftByFactoryEmployee] = useState({});
  const [form, setForm] = useState({ name: '', email: '', password: '', employee_code: '', job_title: '' });
  const [editingId, setEditingId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDetailSiteId, setSelectedDetailSiteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const employeeDetailSectionRef = useRef(null);

  const selectedEmployee = useMemo(
    () => employees.find((e) => String(e.user_id) === selectedEmployeeId),
    [employees, selectedEmployeeId]
  );
  const hasSelectedEmployee = Boolean(selectedEmployee);

  const loadData = async (companyIdParam) => {
    const companyId = companyIdParam || selectedCompanyId || '';
    if (!companyId) {
      setEmployees([]);
      setSites([]);
      return;
    }
    const res = await managementAPI.employeesPageBundle(Number(companyId));
    const bundle = res.data || {};
    const sitesData = bundle.sites || [];
    setEmployees(bundle.employees || []);
    setSites(sitesData);
    setSiteEmployeesBySite(bundle.factory_employees_by_site || {});
    setShiftsBySite(bundle.shifts_by_site || {});
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadData(selectedCompanyId);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'No se pudo cargar empleados');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const assignmentRows = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const rows = [];
    for (const site of sites) {
      const assigned = (siteEmployeesBySite[String(site.id)] || []).find(
        (r) => r.user_id === Number(selectedEmployeeId)
      );
      if (assigned) rows.push({ site, assigned });
    }
    return rows;
  }, [selectedEmployeeId, sites, siteEmployeesBySite]);

  const getAssignedSiteIdsForEmployee = (employeeId) => {
    if (!employeeId) return [];
    const idNum = Number(employeeId);
    return sites
      .filter((site) =>
        (siteEmployeesBySite[String(site.id)] || []).some((row) => Number(row.user_id) === idNum && row.active)
      )
      .map((site) => String(site.id));
  };

  useEffect(() => {
    if (!selectedEmployeeId || assignmentRows.length === 0) {
      setSelectedDetailSiteId('');
      return;
    }
    const exists = assignmentRows.some(({ site }) => String(site.id) === String(selectedDetailSiteId));
    if (!exists) {
      setSelectedDetailSiteId(String(assignmentRows[0].site.id));
    }
  }, [assignmentRows, selectedEmployeeId, selectedDetailSiteId]);

  useEffect(() => {
    (async () => {
      if (!assignmentRows.length) {
        setShiftAssignmentsByFactoryEmployee({});
        return;
      }
      try {
        const entries = await Promise.all(
          assignmentRows.map(async ({ assigned }) => {
            const res = await managementAPI.employeeShifts(assigned.id);
            const rows = res.data || [];
            return [
              String(assigned.id),
              {
                rows,
                selectedShiftIds: rows.map((r) => String(r.shift_id)),
              },
            ];
          })
        );
        setShiftAssignmentsByFactoryEmployee(Object.fromEntries(entries));
      } catch (e) {
        setErr(e?.response?.data?.detail || 'No se pudieron cargar turnos asignados');
      }
    })();
  }, [assignmentRows]);

  const closeFormModal = () => {
    setForm({ name: '', email: '', password: '', employee_code: '', job_title: '' });
    setEditingId(null);
    setShowFormModal(false);
  };

  const saveEmployee = async () => {
    if (!selectedCompanyId) return setErr('Primero selecciona una empresa en el header');
    if (!form.name.trim() || !form.email.trim()) return;
    try {
      setErr('');
      setMsg('');
      if (editingId) {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim(),
          employee_code: form.employee_code.trim() || null,
          job_title: form.job_title.trim() || null,
        };
        if (form.password.trim()) payload.password = form.password.trim();
        await managementAPI.updateEmployee(editingId, Number(selectedCompanyId), payload);
        setMsg('Empleado actualizado');
        await loadData(selectedCompanyId);
        setForm((f) => ({ ...f, password: '' }));
        if (showFormModal) closeFormModal();
        return;
      }
      await managementAPI.createEmployee({
        company_id: Number(selectedCompanyId),
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password.trim() || 'password123',
        employee_code: form.employee_code.trim() || null,
        job_title: form.job_title.trim() || null,
      });
      setMsg('Empleado creado');
      closeFormModal();
      await loadData(selectedCompanyId);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo guardar empleado');
    }
  };

  const openEmployeeDetail = (emp) => {
    setSelectedEmployeeId(String(emp.user_id));
    setEditingId(emp.user_id);
    setForm({
      name: emp.name || '',
      email: emp.email || '',
      password: '',
      employee_code: emp.employee_code || '',
      job_title: emp.job_title || '',
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        employeeDetailSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const deactivateEmployee = async (userId) => {
    if (!selectedCompanyId) return;
    try {
      setErr('');
      setMsg('');
      await managementAPI.deactivateEmployee(userId, Number(selectedCompanyId));
      await loadData(selectedCompanyId);
      if (String(userId) === selectedEmployeeId) {
        setSelectedEmployeeId('');
        setEditingId(null);
        setForm({ name: '', email: '', password: '', employee_code: '', job_title: '' });
      }
      setMsg('Empleado dado de baja');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo dar de baja');
    }
  };

  const assignEmployee = async () => {
    if (!selectedEmployeeId || selectedSiteIds.length === 0) return;
    try {
      setErr('');
      setMsg('');
      const selectedSitesAsNumbers = selectedSiteIds.map((id) => Number(id));
      await Promise.all(
        selectedSitesAsNumbers.map((siteId) =>
          managementAPI.assignFactoryEmployee({
            factory_id: siteId,
            user_id: Number(selectedEmployeeId),
            is_supervisor: false,
            active: true,
          })
        )
      );
      await loadData(selectedCompanyId);
      setMsg(
        selectedSitesAsNumbers.length > 1
          ? `Empleado asignado a ${selectedSitesAsNumbers.length} sedes`
          : 'Empleado asignado a sede'
      );
      setShowAssignModal(false);
      setSelectedSiteIds([]);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo asignar empleado');
    }
  };

  const toggleSiteSelection = (siteId) => {
    setSelectedSiteIds((prev) => {
      const key = String(siteId);
      if (prev.includes(key)) return prev.filter((id) => id !== key);
      return [...prev, key];
    });
  };

  const getShiftGroupsForSite = (siteId) => {
    const rows = shiftsBySite[String(siteId)] || [];
    const grouped = rows.reduce((acc, row) => {
      const key = row.name;
      if (!acc[key]) acc[key] = [];
      acc[key].push(row);
      return acc;
    }, {});
    return Object.entries(grouped).map(([name, items]) => ({
      name,
      items: [...items].sort((a, b) => a.weekday - b.weekday || a.start_time.localeCompare(b.start_time)),
      shiftIds: items.map((i) => String(i.id)),
    }));
  };

  const toggleShiftGroupSelection = (factoryEmployeeId, siteId, groupName) => {
    const key = String(factoryEmployeeId);
    const groups = getShiftGroupsForSite(siteId);
    const group = groups.find((g) => g.name === groupName);
    if (!group) return;
    const groupIds = group.shiftIds;
    setShiftAssignmentsByFactoryEmployee((prev) => {
      const curr = prev[key] || { rows: [], selectedShiftIds: [] };
      const currentlySelected = curr.selectedShiftIds || [];
      const allSelected = groupIds.every((id) => currentlySelected.includes(id));
      const nextSelected = allSelected
        ? currentlySelected.filter((id) => !groupIds.includes(id))
        : Array.from(new Set([...currentlySelected, ...groupIds]));
      return { ...prev, [key]: { ...curr, selectedShiftIds: nextSelected } };
    });
  };

  const saveShiftsForAssignment = async (factoryEmployeeId, siteId) => {
    const key = String(factoryEmployeeId);
    const state = shiftAssignmentsByFactoryEmployee[key] || { rows: [], selectedShiftIds: [] };
    const existingShiftIds = (state.rows || []).map((r) => String(r.shift_id));
    const targetShiftIds = state.selectedShiftIds || [];
    const toCreate = targetShiftIds.filter((id) => !existingShiftIds.includes(id));
    const toDeleteRows = (state.rows || []).filter((r) => !targetShiftIds.includes(String(r.shift_id)));

    try {
      setSavingShiftByFactoryEmployee((prev) => ({ ...prev, [key]: true }));
      setErr('');
      setMsg('');
      await Promise.all([
        ...toDeleteRows.map((r) => managementAPI.deleteEmployeeShift(r.id)),
        ...toCreate.map((shiftId) =>
          managementAPI.assignEmployeeShift({
            factory_employee_id: Number(factoryEmployeeId),
            shift_id: Number(shiftId),
            effective_from: formatLocalDateYmd(),
          })
        ),
      ]);
      const refreshed = await managementAPI.employeeShifts(Number(factoryEmployeeId));
      const rows = refreshed.data || [];
      setShiftAssignmentsByFactoryEmployee((prev) => ({
        ...prev,
        [key]: { rows, selectedShiftIds: rows.map((r) => String(r.shift_id)) },
      }));
      const siteName = sites.find((s) => s.id === Number(siteId))?.name || 'sede';
      setMsg(`Turnos actualizados para ${siteName}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudieron guardar los turnos');
    } finally {
      setSavingShiftByFactoryEmployee((prev) => ({ ...prev, [key]: false }));
    }
  };

  const selectedDetailAssignment = assignmentRows.find(({ site }) => String(site.id) === String(selectedDetailSiteId));

  return (
    <div className="space-y-4">
      <div className="card-mobile">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Empleados</h2>
            <p className="text-sm text-gray-500">Gestiona personal, asignaciones y turnos desde un solo módulo.</p>
          </div>
          <button
            className="btn-mobile btn-primary text-sm py-1.5 px-3"
            onClick={() => {
              setErr('');
              setMsg('');
              setSelectedEmployeeId('');
              setEditingId(null);
              setForm({ name: '', email: '', password: '', employee_code: '', job_title: '' });
              setShowFormModal(true);
            }}
            disabled={loading || !selectedCompanyId}
          >
            + Nuevo Empleado
          </button>
        </div>
      </div>

      <div className="card-mobile">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Listado</h3>
          <span className="text-xs text-gray-500">{employees.length} empleados</span>
        </div>
        {employees.length === 0 ? (
          <p className="text-sm text-gray-500">No hay empleados para esta empresa.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3 font-medium">Nombre</th>
                  <th className="py-2 pr-3 font-medium">Email</th>
                  <th className="py-2 pr-3 font-medium">Puesto</th>
                  <th className="py-2 pr-3 font-medium">Legajo</th>
                  <th className="py-2 pr-3 font-medium">Estado</th>
                  <th className="py-2 pr-1 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => (
                  <tr
                    key={e.user_id}
                    className={`border-b border-gray-100 ${String(e.user_id) === selectedEmployeeId ? 'bg-blue-50/70' : ''}`}
                  >
                    <td className="py-2 pr-3 font-medium text-gray-900">{e.name}</td>
                    <td className="py-2 pr-3 text-gray-600">{e.email}</td>
                    <td className="py-2 pr-3 text-gray-600">{e.job_title || '-'}</td>
                    <td className="py-2 pr-3 text-gray-600">{e.employee_code || '-'}</td>
                    <td className="py-2 pr-3">
                      <span className={`status-badge inline-block ${e.active ? 'success' : 'danger'}`}>
                        {e.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="py-2 pr-1">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="btn-mobile btn-secondary text-xs py-1 px-2 min-h-0"
                          title="Ver detalle y editar"
                          onClick={() => openEmployeeDetail(e)}
                        >
                          👁️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div ref={employeeDetailSectionRef} className="card-mobile scroll-mt-4">
        {!hasSelectedEmployee ? (
          <div>
            <h3 className="font-semibold mb-2">Gestión por empleado</h3>
            <p className="text-sm text-gray-500">
              Seleccioná un empleado desde el listado para abrir su vista de detalle y administrar sedes y turnos.
              Las relaciones de supervisión están en el menú Supervisión.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3 w-full">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Datos del empleado</p>
              <div className="w-full space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2 min-w-0">
                    <span className="text-sm font-semibold text-slate-800 shrink-0 sm:w-24">Nombre</span>
                    <input
                      className="input-mobile flex-1 min-w-0"
                      placeholder="Nombre completo"
                      value={form.name}
                      onChange={(ev) => setForm((f) => ({ ...f, name: ev.target.value }))}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2 min-w-0">
                    <span className="text-sm font-semibold text-slate-800 shrink-0 sm:w-24">Email</span>
                    <input
                      className="input-mobile flex-1 min-w-0"
                      placeholder="correo@ejemplo.com"
                      type="email"
                      autoComplete="off"
                      value={form.email}
                      onChange={(ev) => setForm((f) => ({ ...f, email: ev.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                  <span className="text-sm font-semibold text-slate-800 shrink-0 sm:min-w-[7.5rem] md:min-w-[10rem]">Contraseña</span>
                  <input
                    className="input-mobile flex-1 min-w-0"
                    placeholder="En blanco: no se cambia"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(ev) => setForm((f) => ({ ...f, password: ev.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2 min-w-0">
                    <span className="text-sm font-semibold text-slate-800 shrink-0 sm:min-w-[8rem]">Legajo / código</span>
                    <input
                      className="input-mobile flex-1 min-w-0"
                      value={form.employee_code}
                      onChange={(ev) => setForm((f) => ({ ...f, employee_code: ev.target.value }))}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2 min-w-0">
                    <span className="text-sm font-semibold text-slate-800 shrink-0 sm:w-24">Puesto</span>
                    <input
                      className="input-mobile flex-1 min-w-0"
                      value={form.job_title}
                      onChange={(ev) => setForm((f) => ({ ...f, job_title: ev.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div
                className={`grid gap-2 w-full ${selectedEmployee?.active ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}
              >
                <button
                  type="button"
                  className="btn-mobile btn-primary text-sm py-2 min-h-[44px] w-full"
                  onClick={saveEmployee}
                  disabled={loading || !selectedCompanyId}
                >
                  Guardar cambios
                </button>
                {selectedEmployee?.active && (
                  <button
                    type="button"
                    className="btn-mobile btn-danger text-sm py-2 min-h-[44px] w-full"
                    onClick={() => {
                      if (!window.confirm('¿Dar de baja a este empleado? No podrá ingresar hasta que lo reactives.')) return;
                      deactivateEmployee(selectedEmployee.user_id);
                    }}
                  >
                    Dar de baja empleado
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-4 items-start">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="font-semibold">Sedes asignadas</h3>
                  <button
                    type="button"
                    className="btn-mobile btn-secondary shrink-0 flex h-9 w-9 items-center justify-center rounded-lg p-0 text-lg font-medium leading-none min-h-0"
                    title="Agregar o quitar sedes"
                    aria-label="Agregar o quitar sedes"
                    onClick={() => {
                      setSelectedSiteIds(getAssignedSiteIdsForEmployee(selectedEmployeeId));
                      setShowAssignModal(true);
                    }}
                  >
                    +
                  </button>
                </div>
                {assignmentRows.length === 0 ? (
                  <p className="text-sm text-gray-500">Este empleado no tiene sedes asignadas aún.</p>
                ) : (
                  <div className="space-y-2">
                    {assignmentRows.map(({ site, assigned }) => {
                      const active = String(site.id) === String(selectedDetailSiteId);
                      return (
                        <button
                          key={assigned.id}
                          type="button"
                          className={`w-full text-left rounded-lg border p-2 ${
                            active ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                          onClick={() => setSelectedDetailSiteId(String(site.id))}
                        >
                          <p className="font-medium text-slate-900">{site.name}</p>
                          <p className="text-xs text-slate-500">{site.address}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <div className="rounded-xl border border-slate-200 p-3">
                <h3 className="font-semibold mb-2">Turnos por sede</h3>
                {assignmentRows.length === 0 && (
                  <p className="text-sm text-gray-500">Asigná una sede para gestionar turnos.</p>
                )}
                {selectedDetailAssignment && (() => {
                  const { site, assigned } = selectedDetailAssignment;
                  const shifts = shiftsBySite[String(site.id)] || [];
                  const shiftGroups = getShiftGroupsForSite(site.id);
                  const state = shiftAssignmentsByFactoryEmployee[String(assigned.id)] || { rows: [], selectedShiftIds: [] };
                  return (
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div>
                        <p className="font-semibold text-gray-900">{site.name}</p>
                        <p className="text-xs text-gray-500">{site.address}</p>
                      </div>
                      {shifts.length === 0 ? (
                        <p className="text-xs text-gray-500 mt-2">No hay turnos creados para esta sede.</p>
                      ) : (
                        <div className="mt-2 space-y-1">
                          {shiftGroups.map((group) => {
                            const checked = group.shiftIds.every((id) => state.selectedShiftIds.includes(id));
                            return (
                              <label
                                key={`${site.id}-${group.name}`}
                                className={`flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer ${
                                  checked ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="mt-1"
                                  checked={checked}
                                  onChange={() => toggleShiftGroupSelection(assigned.id, site.id, group.name)}
                                />
                                <span className="text-xs">
                                  <span className="font-medium text-slate-800">{group.name}</span>
                                  <span className="block text-slate-600">
                                    {group.items.map((s) => `${weekdayLabel[s.weekday] || s.weekday} ${s.start_time}-${s.end_time}`).join(' | ')}
                                  </span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn-mobile btn-primary w-full mt-2 text-xs py-1.5"
                        onClick={() => saveShiftsForAssignment(assigned.id, site.id)}
                        disabled={loading || savingShiftByFactoryEmployee[String(assigned.id)]}
                      >
                        {savingShiftByFactoryEmployee[String(assigned.id)] ? 'Guardando...' : 'Guardar turnos de esta sede'}
                      </button>
                    </div>
                  );
                })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="modal-mobile center" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Nuevo empleado</h3>
              <button onClick={closeFormModal} className="text-2xl">×</button>
            </div>
            <div className="modal-content space-y-2">
              <input className="input-mobile" placeholder="Nombre" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <input className="input-mobile" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              <input className="input-mobile" placeholder="Password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              <input className="input-mobile" placeholder="Código empleado (opcional)" value={form.employee_code} onChange={(e) => setForm((f) => ({ ...f, employee_code: e.target.value }))} />
              <input className="input-mobile" placeholder="Puesto (opcional)" value={form.job_title} onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))} />
              <button className="btn-mobile btn-primary w-full mt-2" onClick={saveEmployee} disabled={loading || !selectedCompanyId}>
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && selectedEmployeeId && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-mobile center" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Asignar sedes</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedSiteIds([]);
                }}
                className="text-2xl"
              >
                ×
              </button>
            </div>
            <div className="modal-content space-y-2">
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                <p className="text-xs text-slate-500">Empleado</p>
                <p className="text-sm font-medium text-slate-900">{selectedEmployee?.name || `ID ${selectedEmployeeId}`}</p>
              </div>
              <div className="rounded-xl border border-slate-200 p-2 max-h-44 overflow-auto">
                <p className="text-xs text-slate-500 px-1 pb-1">Sedes (selección múltiple)</p>
                {sites.length === 0 ? (
                  <p className="text-sm text-gray-500 px-1">No hay sedes disponibles.</p>
                ) : (
                  <div className="space-y-1">
                    {sites.map((s) => {
                      const checked = selectedSiteIds.includes(String(s.id));
                      return (
                        <label
                          key={s.id}
                          className={`flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer ${
                            checked ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSiteSelection(s.id)}
                            className="mt-1"
                          />
                          <span className="text-sm">
                            <span className="font-medium text-gray-800">{s.name}</span>
                            <span className="block text-xs text-gray-500">{s.address}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <button className="btn-mobile btn-primary w-full" onClick={assignEmployee} disabled={loading || !selectedEmployeeId || selectedSiteIds.length === 0}>
                Guardar asignación
              </button>
            </div>
          </div>
        </div>
      )}
      <AppToast
        message={err || msg}
        variant={err ? 'error' : 'success'}
        onClose={() => {
          setErr('');
          setMsg('');
        }}
      />
    </div>
  );
};

export default OwnerEmployees;
